import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useEdvisor } from '../../contexts/EdvisorContext';
import EdvisorMessage from './EdvisorMessage';
import EdvisorInput from './EdvisorInput';

interface EdvisorWidgetProps {
  height?: string | number;
  maxHeight?: string | number;
}

const EdvisorWidget: React.FC<EdvisorWidgetProps> = ({ 
  height = '100%', 
  maxHeight = '100%' 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { 
    messages, 
    loading, 
    error, 
    isTyping,
    threadId
  } = useEdvisor();

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    // Add a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Also scroll to bottom when component mounts (for when returning from navigation)
  useEffect(() => {
    if (messages.length > 0) {
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // Empty dependency array means this runs only on mount

  return (
    <Box
      sx={{
        height,
        maxHeight,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Important for flex child to shrink
        width: '100%',
        minWidth: 0, // Allow the container to shrink
        overflow: 'hidden', // Prevent horizontal overflow
      }}
    >

      {/* Loading Bar */}
      {(loading || isTyping) && (
        <LinearProgress 
          sx={{ 
            height: 2,
          }} 
        />
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden', // Prevent horizontal scrolling
          p: isMobile ? 0.5 : 1,
          position: 'relative',
          minHeight: 0, // Important for flex child to shrink
          width: '100%',
          minWidth: 0, // Allow the container to shrink
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[100],
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[300],
            borderRadius: 3,
            '&:hover': {
              backgroundColor: theme.palette.grey[400],
            },
          },
        }}
      >
        {/* Loading State for Initialization */}
        {loading && messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading conversation history...
            </Typography>
          </Box>
        )}

        {/* Welcome State */}
        {!loading && messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Welcome to EdVisor!
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 300 }}>
              I'm here to help you navigate Canadian post-secondary education. 
              Ask me about schools, programs, admissions, or anything else!
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {messages.map((message) => (
          <EdvisorMessage key={message.id} message={message} />
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <EdvisorMessage
            message={{
              id: 'typing',
              content: '',
              sender: 'edvisor',
              timestamp: new Date().toISOString(),
              threadId,
            }}
            isTyping={true}
          />
        )}

        {/* Error Display in Chat */}
        {error && messages.length > 0 && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <EdvisorInput disabled={!!error || loading} />
    </Box>
  );
};

export default EdvisorWidget;
